import { MapPanel } from './map';

export class MapCountyController {
    tab: MapPanel;

    selectedIds = new Set();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    labelMarkers: Record<string, any> = {};
    geojsonLoaded = false;

    GEOJSON_URL = '/counties/counties.json';
    COUNTY_NAME_PROP = 'NAME';
    COUNTY_ID_PROP = 'GEOID';

    dataLayer = new google.maps.Data();

    constructor(tab: MapPanel) {
        this.tab = tab;

        this.dataLayer.setStyle((feature) => {
            const id = feature.getProperty(this.COUNTY_ID_PROP);
            const isSelected = this.selectedIds.has(id);
            return {
                visible: isSelected,
                fillColor: '#1e88e5',
                fillOpacity: isSelected ? 0.35 : 0,
                strokeColor: '#003f7a',
                strokeWeight: 1.2,
                zIndex: isSelected ? 2 : 1,
            };
        });

        this.dataLayer.setMap(this.tab.map.map);
        this.dataLayer.loadGeoJson('/counties/counties.json', null, (features) => {
            this.geojsonLoaded = true;
            // create the collapsible control and pass the select element to populate
            const controls = this.createCollapsibleControl();
            this.populateCountyList(controls.sel);
        });
    }

    private createCollapsibleControl() {
        const wrapper = document.createElement('div');
        wrapper.className = 'map-control-wrapper';

        const toggleBtn = document.createElement('button');
        toggleBtn.className = 'map-control-toggle';
        toggleBtn.title = 'Open county selector';
        toggleBtn.setAttribute('aria-expanded', 'false');
        toggleBtn.innerHTML = '☰';
        Object.assign(toggleBtn.style, {
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            border: 'none',
            background: 'white',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        });

        const panel = document.createElement('div');
        panel.className = 'map-control panel';
        panel.style.display = 'none';
        panel.style.position = 'absolute';
        panel.style.top = '48px';
        panel.style.left = '0';
        panel.style.zIndex = '100';

        panel.innerHTML = `
<div style="padding:10px; background:rgba(255,255,255,0.98); border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.25); font-family:Arial,sans-serif; width:220px;">
<div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px;">
<strong style="font-size:14px">Select counties</strong>
<button id="controlCloseBtn" title="Close" aria-label="Close" style="border:none;background:transparent;cursor:pointer;font-size:16px;">✕</button>
</div>
<div>
<label for="countyList" style="display:block; font-weight:600; margin-bottom:6px; font-size:13px;">(Ctrl/Cmd+click to multi-select)</label>
<select id="countyList" multiple style="width:100%; height:220px; font-size:13px;"></select>
</div>
<div style="margin-top:8px; display:flex; gap:8px; flex-wrap:wrap;">
<button id="drawBtn" style="padding:6px 8px; cursor:pointer;">Draw</button>
<button id="clearBtn" style="padding:6px 8px; cursor:pointer;">Clear</button>
<button id="zoomBtn" style="padding:6px 8px; cursor:pointer;">Zoom</button>
</div>
<div style="margin-top:8px;">
<input type="checkbox" id="showLabels" checked />
<label for="showLabels" style="font-size:13px;">Show labels</label>
</div>
</div>
`;

        wrapper.appendChild(toggleBtn);
        wrapper.appendChild(panel);
        this.tab.map.map.controls[google.maps.ControlPosition.LEFT_TOP].push(wrapper);

        // inner elements refs
        const sel = panel.querySelector('#countyList') as HTMLSelectElement;
        const drawBtn = panel.querySelector('#drawBtn') as HTMLButtonElement;
        const clearBtn = panel.querySelector('#clearBtn') as HTMLButtonElement;
        const zoomBtn = panel.querySelector('#zoomBtn') as HTMLButtonElement;
        const showLabelsCheckbox = panel.querySelector('#showLabels') as HTMLInputElement;
        const closeBtn = panel.querySelector('#controlCloseBtn') as HTMLButtonElement;

        // open/close handlers
        function openPanel() {
            panel.style.display = 'block';
            toggleBtn.setAttribute('aria-expanded', 'true');
            toggleBtn.title = 'Close county selector';
            sel.focus();
            setTimeout(() => document.addEventListener('click', outsideClickListener), 0);
            document.addEventListener('keydown', escListener);
        }
        function closePanel() {
            panel.style.display = 'none';
            toggleBtn.setAttribute('aria-expanded', 'false');
            toggleBtn.title = 'Open county selector';
            document.removeEventListener('click', outsideClickListener);
            document.removeEventListener('keydown', escListener);
        }
        function togglePanel(ev?: Event) {
            ev?.stopPropagation();
            if (panel.style.display === 'none') openPanel();
            else closePanel();
        }
        function outsideClickListener(e: MouseEvent) {
            if (!panel.contains(e.target as Node) && e.target !== toggleBtn) closePanel();
        }
        function escListener(e: KeyboardEvent) {
            if (e.key === 'Escape' || e.key === 'Esc') closePanel();
        }

        toggleBtn.addEventListener('click', togglePanel);
        closeBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            closePanel();
        });

        // button behavior
        drawBtn.addEventListener('click', () => {
            if (!this.geojsonLoaded) alert('GeoJSON still loading — try again in a second.');
            const chosen = Array.from(sel.selectedOptions).map((o) => o.value);
            this.selectedIds.clear();
            chosen.forEach((id) => this.selectedIds.add(id));
            this.refreshStylesAndLabels(showLabelsCheckbox.checked);
        });
        clearBtn.addEventListener('click', () => {
            this.selectedIds.clear();
            this.refreshStylesAndLabels(showLabelsCheckbox.checked);
            for (const opt of Array.from(sel.options)) (opt as HTMLOptionElement).selected = false;
        });
        zoomBtn.addEventListener('click', () => this.zoomToSelection());
        showLabelsCheckbox.addEventListener('change', () => {
            const show = showLabelsCheckbox.checked;
            for (const m of Object.values(this.labelMarkers)) m.setMap(show ? this.tab.map.map : null);
        });

        return { wrapper, panel, toggleBtn, sel, drawBtn, clearBtn, zoomBtn, showLabelsCheckbox, closeBtn };
    }

    private zoomToSelection() {
        if (!this.dataLayer) return;
        const bounds = new google.maps.LatLngBounds();
        let has = false;
        this.dataLayer.forEach((feature) => {
            const id = String(feature.getProperty(this.COUNTY_ID_PROP) ?? '');
            if (this.selectedIds.size === 0 || this.selectedIds.has(id)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                feature.getGeometry()?.forEachLatLng((latlng: any) => {
                    bounds.extend(latlng);
                    has = true;
                });
            }
        });
        if (has) this.tab.map.map.fitBounds(bounds);
    }

    private populateCountyList(selElement: HTMLSelectElement) {
        if (!this.tab.map.map) return;
        const rows: { name: string; id: string }[] = [];
        this.dataLayer.forEach((feature) => {
            const name = String(feature.getProperty(this.COUNTY_NAME_PROP) ?? '(no name)');
            const id = String(feature.getProperty(this.COUNTY_ID_PROP) ?? name);
            rows.push({ name, id });
        });
        rows.sort((a, b) => a.name.localeCompare(b.name));
        selElement.innerHTML = '';
        for (const r of rows) {
            const option = document.createElement('option');
            option.value = r.id;
            option.text = `${r.name} (${r.id})`;
            selElement.appendChild(option);
        }
    }

    private refreshStylesAndLabels(showLabels: boolean) {
        if (!this.dataLayer) return;
        this.dataLayer.setStyle((feature) => {
            const id = String(feature.getProperty(this.COUNTY_ID_PROP) ?? '');
            console.log('ID ' + id);
            const isSelected = this.selectedIds.has(id);
            console.log('SELECTED!');
            return {
                visible: isSelected,
                fillColor: '#1e88e5',
                fillOpacity: isSelected ? 0.35 : 0,
                strokeColor: '#003f7a',
                strokeWeight: 1.2,
                zIndex: isSelected ? 2 : 1,
            };
        });

        // remove existing markers
        for (const k of Object.keys(this.labelMarkers)) {
            this.labelMarkers[k].setMap(null);
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.labelMarkers[k];
        }

        // create label markers for selected features
        if (this.selectedIds.size > 0 && showLabels && this.dataLayer) {
            this.dataLayer.forEach((feature) => {
                const id = String(feature.getProperty(this.COUNTY_ID_PROP) ?? '');
                if (!this.selectedIds.has(id)) return;
                const name = String(feature.getProperty(this.COUNTY_NAME_PROP) ?? id);
                const centroid = this.polygonCentroidForFeature(feature);
                if (!centroid) return;
                const marker = new google.maps.Marker({
                    position: centroid,
                    map: this.tab.map.map,
                    title: name,
                    label: { text: name, fontWeight: '600', fontSize: '11px' },
                    icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 },
                    zIndex: 1000,
                });
                this.labelMarkers[id] = marker;
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private polygonCentroidForFeature(feature: any): any | null {
        const geom = feature.getGeometry();
        if (!geom) return null;
        const type = geom.getType();
        if (type === 'Polygon') {
            const coords = this.googlePolygonToLngLatRings(geom);
            const c = this.centroidOfMultiRings(coords);
            return c ? new google.maps.LatLng(c[1], c[0]) : null;
        } else if (type === 'MultiPolygon') {
            const all: number[][][] = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            geom.getArray().forEach((polygon: any) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                polygon.getArray().forEach((ring: any) => {
                    const arr: number[][] = [];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ring.getArray().forEach((latlng: any) => arr.push([latlng.lng(), latlng.lat()]));
                    all.push(arr);
                });
            });
            const c = this.centroidOfMultiRings(all);
            return c ? new google.maps.LatLng(c[1], c[0]) : null;
        } else {
            return null;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private googlePolygonToLngLatRings(geom: any): number[][][] {
        const rings: number[][][] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        geom.getArray().forEach((loop: any) => {
            const ring: number[][] = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            loop.getArray().forEach((latlng: any) => ring.push([latlng.lng(), latlng.lat()]));
            rings.push(ring);
        });
        return rings;
    }

    private centroidOfMultiRings(rings: number[][][]): number[] | null {
        let totalArea = 0;
        let cx = 0,
            cy = 0;
        for (const ring of rings) {
            if (!ring || ring.length < 3) continue;
            const pts = ring.slice();
            const first = pts[0];
            const last = pts[pts.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) pts.push(first);
            let a = 0,
                sx = 0,
                sy = 0;
            for (let i = 0; i < pts.length - 1; i++) {
                const x0 = pts[i][0],
                    y0 = pts[i][1];
                const x1 = pts[i + 1][0],
                    y1 = pts[i + 1][1];
                const cross = x0 * y1 - x1 * y0;
                a += cross;
                sx += (x0 + x1) * cross;
                sy += (y0 + y1) * cross;
            }
            const area = a / 2;
            if (Math.abs(area) < 1e-12) continue;
            const ringCx = sx / (6 * area);
            const ringCy = sy / (6 * area);
            const ringArea = Math.abs(area);
            cx += ringCx * ringArea;
            cy += ringCy * ringArea;
            totalArea += ringArea;
        }
        if (totalArea === 0) return null;
        return [cx / totalArea, cy / totalArea];
    }
}
